import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Plus, History, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { api, Category } from '../../services/api';
import { CategoryEditDialog } from './CategoryEditDialog';
import { CategoryAuditDialog } from './CategoryAuditDialog';
import { ConfirmationDialog } from './ConfirmationDialog';
import { cn } from '../../lib/utils';

interface CategoryListProps {
  onCategorySelect?: (category: Category) => void;
  selectedCategoryId?: number;
  showActions?: boolean;
  isAdmin?: boolean;
}

export const CategoryList: React.FC<CategoryListProps> = ({
  onCategorySelect,
  selectedCategoryId,
  showActions = true,
  isAdmin = false
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deactivationError, setDeactivationError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showingAudit, setShowingAudit] = useState<Category | null>(null);
  const [deactivatingCategory, setDeactivatingCategory] = useState<Category | null>(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getCategories();
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
  };

  const handleSaveEdit = async (updatedCategory: Category) => {
    // Update local state optimistically
    setCategories(prev => 
      prev.map(cat => cat.id === updatedCategory.id ? updatedCategory : cat)
    );
    setEditingCategory(null);
  };

  const handleDeactivate = (category: Category) => {
    setDeactivatingCategory(category);
  };

  const handleConfirmDeactivate = async (reason?: string) => {
    if (!deactivatingCategory) return;

    try {
      await api.deactivateCategory(deactivatingCategory.id, reason);
      // Remove from local state
      setCategories(prev =>
        prev.filter(cat => cat.id !== deactivatingCategory.id)
      );
      setDeactivatingCategory(null);
      setDeactivationError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to deactivate category';
      const friendly = msg.includes('403')
        ? "You don't have permission to deactivate categories. Only admins can perform this action."
        : msg;
      setDeactivationError(friendly);
      setDeactivatingCategory(null);
    }
  };

  const handleShowAudit = (category: Category) => {
    setShowingAudit(category);
  };

  if (loading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={loadCategories}
          className="ml-auto"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showActions && (
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Categories</h2>
          {isAdmin && (
            <Button
              onClick={() => setEditingCategory({} as Category)}
              size="sm"
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          )}
        </div>
      )}

      {deactivationError && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-sm">{deactivationError}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeactivationError(null)}
            className="ml-auto text-red-800 hover:text-red-900 hover:bg-red-100 h-6 px-2"
          >
            Dismiss
          </Button>
        </div>
      )}

      <div className="grid gap-3">
        {categories.map((category) => (
          <Card 
            key={category.id}
            className={cn(
              "group p-4 transition-all hover:shadow-md cursor-pointer",
              selectedCategoryId === category.id && "ring-2 ring-primary",
              !category.isActive && "opacity-60 bg-gray-50"
            )}
            onClick={() => onCategorySelect?.(category)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full border-2 border-gray-300"
                  style={{ backgroundColor: category.color }}
                />
                <div>
                  <h3 className="font-medium">{category.name}</h3>
                  {category.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {category.description}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {!category.isActive && (
                  <Badge variant="secondary" className="text-xs">
                    Inactive
                  </Badge>
                )}
                
                {showActions && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowAudit(category);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(category);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeactivate(category);
                        }}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
              <span>
                Created {new Date(category.createdAt).toLocaleDateString()}
                {category.createdBy && ` by ${category.createdBy}`}
              </span>
              {category.updatedAt && category.updatedAt !== category.createdAt && (
                <span>
                  Updated {new Date(category.updatedAt).toLocaleDateString()}
                  {category.updatedBy && ` by ${category.updatedBy}`}
                </span>
              )}
            </div>
          </Card>
        ))}
      </div>

      {categories.length === 0 && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          <div className="text-4xl mb-2">📂</div>
          <p>No categories found</p>
          {showActions && isAdmin && (
            <Button
              variant="outline"
              className="mt-4 gap-2"
              onClick={() => setEditingCategory({} as Category)}
            >
              <Plus className="h-4 w-4" />
              Create First Category
            </Button>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      {editingCategory && (
        <CategoryEditDialog
          category={editingCategory.id ? editingCategory : null}
          open={!!editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Audit Dialog */}
      {showingAudit && (
        <CategoryAuditDialog
          category={showingAudit}
          open={!!showingAudit}
          onClose={() => setShowingAudit(null)}
        />
      )}

      {/* Deactivation Confirmation */}
      {deactivatingCategory && (
        <ConfirmationDialog
          open={!!deactivatingCategory}
          onClose={() => setDeactivatingCategory(null)}
          onConfirm={handleConfirmDeactivate}
          title="Deactivate Category"
          description={`Are you sure you want to deactivate "${deactivatingCategory.name}"? This will hide it from new prompts but existing prompts will keep their category.`}
          confirmText="Deactivate"
          destructive
          requireReason
        />
      )}
    </div>
  );
};